/* Journal Contact — colophon-style. Studio details in a hanging Roman-numeral
   list. Form on the right as a print-card. Submit is a gold serif italic link,
   not a button. */

import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useTenantTheme } from "@/hooks/useTenantTheme";

export default function JournalContact() {
  useTenantTheme();
  const { settings } = useSiteSettings();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (!form.email || !form.message) return;
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Your letter has been received.");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Could not deliver.");
    } finally {
      setSending(false);
    }
  }

  const colophon = [
    { num: "I.", label: "Studio", value: settings?.studio_name || "—" },
    { num: "II.", label: "Address", value: settings?.contact_location || "By appointment" },
    { num: "III.", label: "Correspondence", value: settings?.contact_email || "—", href: settings?.contact_email ? `mailto:${settings.contact_email}` : null },
    { num: "IV.", label: "Hours", value: settings?.contact_hours || "Tuesday — Saturday, by sitting" },
  ];

  return (
    <div
      className="min-h-screen"
      style={{ background: "#F5F2EC", color: "#0E0D0C", fontFamily: "var(--tenant-serif)" }}
      data-testid="journal-contact"
    >
      <section className="pt-16 pb-10 px-6 lg:px-12 max-w-5xl mx-auto text-center">
        <p className="text-[10px] uppercase tracking-[0.42em] text-[color:var(--tenant-accent)] mb-4">
          Colophon · Back matter
        </p>
        <h1 className="font-light italic" style={{ fontFamily: "var(--tenant-serif)", fontSize: "clamp(3rem, 8vw, 6rem)" }}>
          Correspondence
        </h1>
      </section>

      <div className="max-w-5xl mx-auto px-6 lg:px-12 pb-24 grid grid-cols-1 lg:grid-cols-2 gap-16">
        {/* LEFT — colophon list */}
        <aside className="space-y-7">
          <p className="text-[10px] uppercase tracking-[0.42em] text-[color:var(--tenant-accent)] mb-4">
            The studio
          </p>
          {colophon.map((c) => (
            <div key={c.num} className="grid grid-cols-[2.5rem_1fr] gap-3 items-baseline">
              <span className="italic text-[color:var(--tenant-accent)]" style={{ fontFamily: "var(--tenant-serif)" }}>{c.num}</span>
              <div>
                <div className="text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-fg,#0E0D0C)]/55 mb-1">{c.label}</div>
                {c.href ? (
                  <a href={c.href} className="italic text-lg hover:opacity-70 transition-opacity" style={{ fontFamily: "var(--tenant-serif)", borderBottom: "1px solid var(--tenant-accent, #C9A961)" }}>
                    {c.value}
                  </a>
                ) : (
                  <div className="italic text-lg" style={{ fontFamily: "var(--tenant-serif)" }}>{c.value}</div>
                )}
              </div>
            </div>
          ))}
        </aside>

        {/* RIGHT — print-card form */}
        <div
          className="p-8 lg:p-10"
          style={{ background: "#FAF8F2", border: "1px solid var(--tenant-accent-line, rgba(201,169,97,0.35))", boxShadow: "0 1px 0 rgba(0,0,0,0.04)" }}
          data-testid="journal-contact-card"
        >
          <p className="text-[10px] uppercase tracking-[0.42em] text-[color:var(--tenant-accent)] mb-4">
            Post a letter
          </p>
          <form onSubmit={submit} className="space-y-7" data-testid="journal-contact-form">
            {[
              { name: "name", label: "Your name", type: "text" },
              { name: "email", label: "Your address", type: "email" },
            ].map((f) => (
              <div key={f.name}>
                <label className="block text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-fg,#0E0D0C)]/55 mb-2">{f.label}</label>
                <input
                  type={f.type}
                  value={form[f.name]}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  className="w-full bg-transparent border-0 border-b focus:outline-none py-1 text-lg italic"
                  style={{ borderColor: "var(--tenant-accent-line, rgba(201,169,97,0.35))", fontFamily: "var(--tenant-serif)" }}
                  data-testid={`journal-contact-${f.name}`}
                />
              </div>
            ))}
            <div>
              <label className="block text-[10px] uppercase tracking-[0.32em] text-[color:var(--tenant-fg,#0E0D0C)]/55 mb-2">Your letter</label>
              <textarea
                rows={5}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full bg-transparent border-0 border-b focus:outline-none py-1 text-base italic resize-none leading-relaxed"
                style={{ borderColor: "var(--tenant-accent-line, rgba(201,169,97,0.35))", fontFamily: "var(--tenant-serif)" }}
                data-testid="journal-contact-message"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              className="italic text-lg text-[color:var(--tenant-accent)] disabled:opacity-50 hover:opacity-70 transition-opacity"
              style={{ fontFamily: "var(--tenant-serif)", borderBottom: "1px solid var(--tenant-accent, #C9A961)" }}
              data-testid="journal-contact-submit"
            >
              {sending ? "Posting…" : "Post your letter →"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
